import { c as _c } from "react/compiler-runtime";
import { useCompanyId } from "@context-providers/company-id-provider";
import { isCompanyMember } from "@custom-types/member-type-guards";
import { findGroupMember, getGroupManagers, getManagerNotes, sortCandidatesByName } from "@pages/project-details/project-overview/change-management/change-management-utils";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { companyMembersSelector } from "@store/members/members-selector";
import { activeSeatUsageSelector } from "@store/sdb-company/sdb-company-selector";
import { fetchCompanyMembers } from "@store/members/members-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { toGroupMember } from "@utils/member-utils";
import { useEffect, useMemo } from "react";

/**
 * Returns the group and project managers of the group who can manage the project. Teams can only
 * manage projects on a plan that does not limit the Sphere XG seats, since a team cannot hold one.
 */
function getGroupManagerCandidates(groupMembers, canChooseTeams) {
  return getGroupManagers(groupMembers).filter(member => canChooseTeams || !FaroCoreApiUtils.Member.isTeam(member));
}

/**
 * Returns the members of the workspace as candidates, together with the members of the group the
 * workspace members in the store do not list, such as somebody just invited by email, and the teams
 * managing projects in the group when teams can be chosen. The workspace members never list teams.
 */
function getWorkspaceCandidates(workspaceUsers, groupMembers, canChooseTeams) {
  const workspaceCandidates = workspaceUsers.map(toGroupMember);
  return [...workspaceCandidates, ...groupMembers.filter(member => !FaroCoreApiUtils.Member.isTeam(member) && !findGroupMember(member, workspaceCandidates)), ...getGroupManagerCandidates(groupMembers, canChooseTeams).filter(member => FaroCoreApiUtils.Member.isTeam(member))];
}
/**
 * Returns who can be chosen as the project manager when changing the management of a project.
 *
 * An admin of the workspace can choose any member of the workspace, since they can add them to the
 * group too, with a note on their role in the group. Anybody else can only choose the group and
 * project managers of the selected group. Teams managing projects in the group can be chosen too,
 * unless the plan limits the Sphere XG seats (the `ACTIVE_SEAT` constraint).
 *
 * @param groupMembers The members of the selected group
 * @param isOpen Whether the dialog is open, so that the members of the workspace are only fetched
 * when they are needed
 */
export function useManagerCandidates(groupMembers, isOpen) {
  const $ = _c(20);
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const currentUser = useAppSelector(currentUserSelector);
  const workspaceMembers = useAppSelector(companyMembersSelector);
  const isWorkspaceAdminView = currentUser?.role === "COMPANY_EXECUTIVE";
  const canChooseTeams = useAppSelector(activeSeatUsageSelector) === null;
  let t0;
  let t1;
  if ($[0] !== companyId || $[1] !== dispatch || $[2] !== isOpen || $[3] !== isWorkspaceAdminView) {
    t0 = () => {
      if (isOpen && isWorkspaceAdminView) {
        dispatch(fetchCompanyMembers({
          companyId
        }));
      }
    };
    t1 = [companyId, dispatch, isOpen, isWorkspaceAdminView];
    $[0] = companyId;
    $[1] = dispatch;
    $[2] = isOpen;
    $[3] = isWorkspaceAdminView;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  let t2;
  if ($[6] !== workspaceMembers) {
    t2 = workspaceMembers.filter(isCompanyMember);
    $[6] = workspaceMembers;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const workspaceUsers = t2;
  let t3;
  if ($[8] !== canChooseTeams || $[9] !== groupMembers || $[10] !== isWorkspaceAdminView || $[11] !== workspaceUsers) {
    t3 = sortCandidatesByName(isWorkspaceAdminView ? getWorkspaceCandidates(workspaceUsers, groupMembers, canChooseTeams) : getGroupManagerCandidates(groupMembers, canChooseTeams));
    $[8] = canChooseTeams;
    $[9] = groupMembers;
    $[10] = isWorkspaceAdminView;
    $[11] = workspaceUsers;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const candidates = t3;
  let t4;
  if ($[13] !== candidates || $[14] !== groupMembers) {
    t4 = getManagerNotes(candidates, groupMembers);
    $[13] = candidates;
    $[14] = groupMembers;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const memberNotes = t4;
  let t5;
  if ($[16] !== candidates || $[17] !== isWorkspaceAdminView || $[18] !== memberNotes) {
    t5 = {
      isWorkspaceAdminView,
      candidates,
      memberNotes
    };
    $[16] = candidates;
    $[17] = isWorkspaceAdminView;
    $[18] = memberNotes;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}